import { loadGameData, loadDbSongs } from './data.js';
import { initConstants } from './constants.js';
import { initAuthUI, openLoginModal } from './auth-ui.js';
import { initSettingsUI } from './settings-ui.js';
import { gameEvents, EVT } from './events.js';
import { currentUser, updateProfile } from './auth.js';
import { unlockAudio } from './audio.js';
import { SCENE } from './keys.js';

// PWA: register on deployed origins only — keep localhost dev SW-free so
// the "edit and refresh" workflow never serves stale modules. (Lives here
// rather than inline in game.html so the CSP can forbid inline scripts.)
if ('serviceWorker' in navigator
    && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

(async () => {
  // Data is the long pole of boot. Load it concurrently with the web font
  // instead of awaiting the font afterwards — the font is no longer a
  // serial stall, but text still renders crisp because both finish before
  // any scene mounts.
  await Promise.all([loadGameData(), document.fonts.ready]);
  // DB-stored compositions only augment the bundled songs (which play
  // meanwhile), so this stays off the boot critical path — fire and forget.
  loadDbSongs();
  initConstants();
  initAuthUI();      // fire-and-forget; HUD shows up once /api/me resolves
  initSettingsUI();  // ⚙ icon in top-right → audio + controls + future settings

  // Keep WebAudio alive across the app's whole lifecycle.
  //
  // Gesture: iOS/Android only let resume() succeed inside a user
  // gesture, so any tap/key anywhere unlocks — not just one Phaser
  // button. This stays armed (not one-shot): after a PWA is backgrounded
  // the context dies, and the next tap must be able to revive it.
  //
  // Foreground: reopening a backgrounded PWA fires visibilitychange /
  // pageshow; try to resume then too (and unlockAudio re-arms the mood).
  // unlockAudio is a cheap no-op once the context is already running.
  {
    const wake = () => unlockAudio();
    for (const e of ['pointerdown', 'keydown', 'touchstart']) {
      window.addEventListener(e, wake, { capture: true, passive: true });
    }
    document.addEventListener('visibilitychange', () => { if (!document.hidden) wake(); });
    window.addEventListener('pageshow', wake);
  }

  // When the tutorial wraps up, an anonymous player has just added their
  // first book — the natural moment to invite them to join the club so
  // that collection follows them across devices.
  gameEvents.on(EVT.TUT_FINISHED, () => {
    // Anonymous finisher → invite them to save it. Signed-in finisher
    // (e.g. routed through onboarding) → latch the DB flag now.
    if (currentUser()) updateProfile({ onboarded: true }).catch(() => {});
    else openLoginModal({ mode: 'new' });
  });

  // First paint only needs Boot + Start (the title / opening cinematic),
  // so import just those two eagerly. They're imported after data +
  // constants are ready so their module-level code (and the renderers they
  // reach into) sees fully populated values.
  const [{ BootScene }, { StartScene }] = await Promise.all([
    import('./scenes/BootScene.js'),
    import('./scenes/StartScene.js'),
  ]);

  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game',
    backgroundColor: '#05051a',
    physics: { default: 'arcade', arcade: { gravity: { y: 0 }, debug: false } },
    input: { activePointers: 4 },
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.NO_CENTER,
      width: window.innerWidth,
      height: window.innerHeight,
    },
    scene: [BootScene, StartScene],
  });

  // Stream the remaining levels in navigation order and register each as it
  // arrives — keeping ~200 KB of level code off the first-paint path. These
  // load concurrently the moment the game boots, so every scene is
  // registered well before the player can fade out of the title screen
  // (Start → Intro/Town only fires after the cinematic or a menu tap). One
  // scene failing to load must not wedge the others, hence the per-scene
  // catch.
  const LAZY_SCENES = [
    [SCENE.INTRO,    () => import('./scenes/IntroScene.js'),    'IntroScene'],
    [SCENE.TOWN,     () => import('./scenes/TownScene.js'),     'TownScene'],
    [SCENE.LIBRARY,  () => import('./scenes/LibraryScene.js'),  'LibraryScene'],
    [SCENE.CRIB,     () => import('./scenes/CribScene.js'),     'CribScene'],
    [SCENE.ROCKET,   () => import('./scenes/RocketScene.js'),   'RocketScene'],
    [SCENE.HANG,     () => import('./scenes/HangScene.js'),     'HangScene'],
    [SCENE.ELEVATOR, () => import('./scenes/ElevatorScene.js'), 'ElevatorScene'],
  ];
  for (const [key, load, name] of LAZY_SCENES) {
    load()
      .then((m) => { if (!game.scene.getScene(key)) game.scene.add(key, m[name]); })
      .catch(() => {});
  }

  // Expose the running game on window for Playwright (and for quick
  // dev-tools debugging). Harmless in production — anyone digging in
  // dev tools could reach it anyway.
  window.__bbcGame = game;

  // When a sign-in / device hydrate pulls a new appearance into the
  // local cache, regenerate the shared 'player' texture so the avatar
  // in whatever scene is live updates immediately (the texture is
  // otherwise only rebuilt when the mirror panel is used).
  gameEvents.on(EVT.APPEARANCE_SYNCED, async () => {
    // Dynamic import: player.js reads game data at module-init, so it
    // stays out of the eager boot graph. It's already cached here (the
    // scenes loaded it), so this resolves instantly.
    const { makePlayerTexture } = await import('./player.js');
    for (const s of game.scene.getScenes(true)) {
      if (!s.textures) continue;
      makePlayerTexture(s);
      if (s.player && s.player.setTexture) s.player.setTexture('player');
    }
  });

  // Pause Phaser's keyboard handler while the player is typing in any
  // text input. Without this, the movement keys we claim with
  // addKey() (A/D/W/S/E and friends) get `preventDefault`-ed by
  // Phaser and never reach the input — so typing "wasd" into a book
  // title field silently moves the player around the world instead.
  //
  // Listening at the document level is a once-and-done fix that
  // covers every DOM overlay automatically: add-book modal, auth
  // modal, settings, future ones we haven't written yet.
  const isTextInput = (el) => {
    if (!el) return false;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
  };
  const syncKeyboard = () => {
    game.input.keyboard.enabled = !isTextInput(document.activeElement);
  };
  document.addEventListener('focusin',  syncKeyboard);
  // focusout fires before the next focusin, so defer one tick — by
  // then activeElement reflects the new focus target (or <body>).
  document.addEventListener('focusout', () => setTimeout(syncKeyboard, 0));
})();
